// Domain enums shared by the API, web and mobile apps. Values match the
// desktop app (desktop/internal/model/model.go) so data imported from it
// needs no translation.

export const ORDER_STATUSES = [
  'confirmed',
  'preorder',
  'delayed',
  'payment_failed',
  'ready_for_pickup',
  'shipped',
  'out_for_delivery',
  'delivered',
  'cancelled',
  'refunded',
] as const
export type OrderStatus = (typeof ORDER_STATUSES)[number]

/**
 * Statuses only move forward: an update applies when its priority is at
 * least the current one. Delivered and cancelled are terminal peers.
 */
export const STATUS_PRIORITY: Record<OrderStatus, number> = {
  confirmed: 1,
  preorder: 2,
  delayed: 2,
  payment_failed: 2,
  ready_for_pickup: 3,
  shipped: 4,
  out_for_delivery: 5,
  delivered: 6,
  cancelled: 6,
  refunded: 7,
}

/** An order counts as a success ("kept") unless the retailer took it back. */
export const isKept = (status: OrderStatus) => status !== 'cancelled' && status !== 'refunded'

export const RETAILERS = ['target', 'bestbuy', 'walmart', 'nike', 'pokemoncenter', 'adidas', 'mattel', 'topps'] as const
export type Retailer = (typeof RETAILERS)[number]

export const RETAILER_LABELS: Record<Retailer, string> = {
  target: 'Target',
  bestbuy: 'Best Buy',
  walmart: 'Walmart',
  nike: 'Nike',
  pokemoncenter: 'Pokémon Center',
  adidas: 'adidas',
  mattel: 'Mattel Creations',
  topps: 'Topps',
}

export const CARRIERS = ['UPS', 'FedEx', 'USPS', 'OnTrac', 'LaserShip', 'DHL'] as const
export type Carrier = (typeof CARRIERS)[number]

export const DISCORD_ALERT_KINDS = ['checkout', 'hold', 'cancelled', 'declined', 'failed'] as const
export type DiscordAlertKind = (typeof DISCORD_ALERT_KINDS)[number]

/** Where a row entered the platform. Only `desktop` and `seed` exist today. */
export const INGEST_SOURCES = ['desktop', 'forward', 'imap', 'seed'] as const
export type IngestSource = (typeof INGEST_SOURCES)[number]

export const ORG_ROLES = ['owner', 'admin', 'member'] as const
export type OrgRole = (typeof ORG_ROLES)[number]

/**
 * Order events a phone can be told about. Values are the `event` field of the
 * push payload and the Settings toggles (iOS: Data/Notifications.swift).
 */
export const ORDER_EVENTS = ['placed', 'shipped', 'delivered'] as const
export type OrderEvent = (typeof ORDER_EVENTS)[number]

/** Push channels. Only APNs (the iOS app) is delivered today. */
export const PUSH_PLATFORMS = ['apns'] as const
export type PushPlatform = (typeof PUSH_PLATFORMS)[number]

/** APNs has two gateways; a token only works on the one its build was signed for. */
export const PUSH_ENVIRONMENTS = ['production', 'sandbox'] as const
export type PushEnvironment = (typeof PUSH_ENVIRONMENTS)[number]
